import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Input from "../../theme/untitled/Input/Input";
import ConfirmDialog from "../../theme/untitled/ConfirmDialog/ConfirmDialog";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./SettingsPage.module.scss";
import { parseGeneralError } from "./errorHelpers";

const HELP_ARTICLE_URL =
  "https://help.revisual.io/en/articles/13610143-how-to-delete-your-revisual-account";

const DeleteAccountCard = ({ email, user, deleteAccount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");

  const deleting = user.deleteAccount;

  useEffect(() => {
    if (user.deleteAccountError) {
      setConfirmOpen(false);
    }
  }, [user.deleteAccountError]);

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setConfirmEmail("");
  };

  const onConfirmDelete = () => {
    deleteAccount();
  };

  const errorMessage = user.deleteAccountError
    ? parseGeneralError(
        user.deleteAccountError,
        "The service is unavailable. Try again in a moment."
      )
    : null;

  return (
    <Card aria-labelledby="delete-account-title">
      <Card.Header
        className={classNames(styles.clickableHeader, {
          [styles.noHeaderBorder]: !isOpen,
        })}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className={styles.deleteHeader}>
          <div className={styles.deleteHeaderText}>
            <h2 id="delete-account-title">Delete account</h2>
            <p>
              Removes your profile and everything only you own.{" "}
              <a
                className={styles.deleteLink}
                href={HELP_ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                How account deletion works
                <Icon name="Share04" size={14} className={styles.deleteLinkIcon} />
              </a>
            </p>
          </div>
          <Button
            hierarchy="secondaryGray"
            size="sm"
            aria-expanded={isOpen}
            iconTrailing={
              <Icon
                name="ChevronDown"
                size={16}
                className={`${styles.disclosureChevron} ${
                  isOpen ? styles["disclosureChevron--open"] : ""
                }`}
              />
            }
            className={styles.disclosureButton}
          >
            <span className={styles.disclosureLabel}>
              {isOpen ? "Hide details" : "Show details"}
            </span>
          </Button>
        </div>
      </Card.Header>

      {isOpen && (
        <>
          <Card.Body>
            <div className={styles.alert}>
              <Icon name="AlertTriangle" size={20} className={styles.alertIcon} />
              <div className={styles.alertText}>
                <strong>This is permanent.</strong> Deleted accounts, workspaces and widget
                data cannot be restored.
              </div>
            </div>
            <div className={styles.prose}>
              <p>The impact on your workspaces depends on your role in each one:</p>
              <ul>
                <li>
                  <strong>Workspaces where you are the only Owner</strong> are deleted with the
                  account.
                </li>
                <li>
                  <strong>Workspaces with other members, or where you are not the Owner</strong>{" "}
                  stay active for those members.
                </li>
              </ul>
              <p className={styles.eyebrow}>When a workspace is deleted</p>
              <ul>
                <li>All workspace data, integrations, widgets and connected websites are removed</li>
                <li>Financial records are kept for as long as the law requires</li>
              </ul>
              <p>You are signed out as soon as deletion completes.</p>
            </div>
          </Card.Body>
          <Card.Footer className={styles.cardFooter}>
            {errorMessage ? (
              <span className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}>
                <Icon
                  name="AlertCircle"
                  size={16}
                  className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
                />
                <span>
                  <strong>Couldn't delete your account.</strong> {errorMessage}
                </span>
              </span>
            ) : (
              <span className={styles.footerNote}>
                Deleting removes your profile and any workspace you solely own.
              </span>
            )}
            <div className={styles.cardFooterActions}>
              <Button hierarchy="destructive" onClick={() => setConfirmOpen(true)}>
                {errorMessage ? "Try again" : "Delete account"}
              </Button>
            </div>
          </Card.Footer>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={onConfirmDelete}
        icon={<Icon name="AlertTriangle" size={24} />}
        title="Delete your account?"
        confirmLabel={deleting ? "Deleting…" : "Delete account"}
        confirmDisabled={confirmEmail !== email}
        busy={deleting}
      >
        <p>This cannot be undone. Type your email address to confirm.</p>
        <div className={styles.confirmField}>
          <Input
            label="Confirm your email"
            hint={email}
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            disabled={deleting}
            autoComplete="off"
          />
        </div>
      </ConfirmDialog>
    </Card>
  );
};

export default DeleteAccountCard;
